<template>
  <ContentSection>
    <div
      ref="el"
      class="
        relative overflow-hidden
        max-lg:px-container
        max-xl:px-container-desktop-half
        xl:h-[300vw] xl:max-h-[calc(100vh-var(--navbar-height)-200px)]
      "
    >
      <div
        v-for="highlight, index in data.highlights"
        :key="highlight.id"
        :ref="(el) => {
          slides[index] = el as ComponentPublicInstance
        }"
        class="flex max-xl:mb-6 max-xl:flex-col-reverse xl:flex-row"
      >
        <div
          id="textContainer"
          class="
            z-10 flex flex-col justify-center bg-grey-100 px-container py-14
            lg:px-container-desktop
            md:px-container-desktop-half
            xl:absolute xl:left-0 xl:top-0 xl:aspect-square xl:h-full xl:w-1/2
          "
        >
          <HeadingGroup
            v-if="highlight.heading"
            class="typo-h3"
            :heading="highlight.heading"
            :sub-heading="highlight.sub_heading"
          />
          <div class="mt-6 max-w-text typo-lead" v-html="highlight.text" />
          <span v-if="hasSlider" class="bottom-18 mt-12 text-grey-300 max-xl:hidden xl:absolute">{{ index + 1 }}/{{ data.highlights.length }}</span>
          <AppButton
            v-if="highlight.link && highlight.link_text"
            class="mt-8"
            :to="highlight.link"
            variant="primary"
          >
            {{ highlight.link_text }}
          </AppButton>
        </div>
        <div
          id="imageContainer"
          class="max-xl:overflow-hidden xl:absolute xl:right-0 xl:top-0 xl:size-full xl:w-1/2"
        >
          <AppImage
            :src="highlight.image"
            class="object-cover max-xl:max-h-[50vh] max-xl:w-full xl:size-full xl:scale-[1.2]"
          />
        </div>
      </div>
    </div>
  </ContentSection>
</template>

<script setup lang="ts">
import { gsap } from 'gsap'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import type { MainComponentsHighlightsFragment } from '#gql'

gsap.registerPlugin(ScrollTrigger)

const props = defineProps<{
  data: Enforce<MainComponentsHighlightsFragment>
}>()

const hasSlider = computed(() => props.data.highlights.length > 1)

const el = ref<HTMLElement | undefined>(undefined)
const slides = ref<ComponentPublicInstance[]>([])
let ctx: gsap.Context | null = null

const initScrolltrigger = () => {
  ctx = useGsap.context((self) => {
    if (!self || !self.selector) return

    const mm = gsap.matchMedia()

    let startValue = 'top 150px'
    mm.add('(min-width: 1536px)', () => {
      startValue = 'top 200px'
    })
    mm.add('(min-width: 1920px)', () => {
      startValue = 'top 200px' // maybe we can find a solution to work with the rems on large screens
    })

    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: el.value,
        pin: true,

        start: startValue,
        end: () => `+=${(slides.value.length - 1) * 100}%`,
        scrub: 1.25,
        markers: false,
      },
    })

    slides.value.forEach((slide, index) => {
      if (!slide) return

      const slideEl = slide instanceof HTMLElement ? slide : slide.$el
      const imageContainer = slideEl.querySelector('#imageContainer')
      const image = imageContainer instanceof HTMLElement ? imageContainer.querySelector('img') : null
      const textContainer = slideEl.querySelector('#textContainer')

      if (imageContainer) {
        // Overlap the next image over the current one
        tl.fromTo(
          imageContainer,
          {
            zIndex: index,
            y: index === 0 ? 0 : `${(index) * 100}%`,
          },
          {
            y: 0,
            duration: 2,
            ease: 'power2.out',
          },
          index * 1, // Stagger timing for each slide
        )
      }

      if (image) {
        tl.fromTo(
          image,
          {
            y: '10%',
          },
          {
            y: '-10%',
            duration: 5,
          },
          index * 1, // Stagger timing for each slide
        )
      }

      if (textContainer) {
        // Ensure the first text is visible before the pin is triggered
        if (index === 0) {
          gsap.set(textContainer, {
            autoAlpha: 1, // Make the first text visible from the start
          })
        } else {
          gsap.set(textContainer, {
            autoAlpha: 0, // Hide the other texts
          })
        }

        if (index > 0) {
        // Transition to make the next text visible once its associated image passes the threshold
          tl.fromTo(
            textContainer,
            {
              autoAlpha: 0,
            },
            {
              autoAlpha: 1,
              duration: 0.1,
              ease: 'power2.out',
            },
            index * 1.25,
          )
        }
      }
    })
  }, el.value)
}

function initSlider() {
  ctx?.revert()
  if (hasSlider.value && window.innerWidth > 1280) {
    initScrolltrigger()
    ScrollTrigger.refresh()
  }
}

function onResize() {
  if (!hasSlider.value) return
  initSlider()
}

const debouncedOnResize = useDebounceFn(onResize, 500)

onMounted(() => {
  nextTick(() => {
    initSlider()
    if (hasSlider.value) {
      window.addEventListener('resize', debouncedOnResize)
    }
  })
})

onUnmounted(() => {
  ctx?.revert()
  window.removeEventListener('resize', debouncedOnResize)
})
</script>
